import { useEffect, useRef, useState } from 'react';
import Toast from '../ui/toasts/Toast';
import {
    getUserPreferences,
    updateCommunicationPreferences,
} from '../../services/individualService';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import PhoneInput from 'react-phone-number-input/input';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { isValidPhoneNumber } from '../../utils/validate';
import { formatPhoneNumber } from '../../utils/utils';
import useNotifications from '../../hooks/useNotifications';
import style from '../ui/modals/modals.module.scss';
import { useAuthContext } from '../../store/Contexts';
import { getClientOptions } from '../../services/clientService';
import Tooltip from '../ui/tooltips/Tooltip';
import LoadingWrapper from '../ui/loaders/LoadingWrapper';
import { getText } from '../../i18n';

const OPT_OUT_CATEGORIES_CLIENT_OPTION_ID = 12;

const NOTIFICATION_METHOD = {
    primaryEmail: 1,
    primaryPhone: 2,
    pushNotification: 3,
    optionalEmail: 4,
    optionalPhone: 5,
};

const CommunicationPreferences = ({
    onYes = null,
    onNo = null,
    onHide = null,
    isModal = false,
}) => {
    const footerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useAuthContext();
    const [validationMessages, setValidationMessages] = useState({
        emailAddress: '',
        phoneNumber: '',
    });
    const { requestNotificationPermission, notificationsEnabled } =
        useNotifications();

    const [preferences, setPreferences] = useState({
        fileEmailAddress: '',
        filePhoneNumber: '',
        emailAddress: '',
        phoneNumber: '',
        notificationPreferredMethodId: NOTIFICATION_METHOD.primaryEmail,
        notificationCategoryOptOuts: [],
    });

    useEffect(() => {
        loadUserPreferences();
        initializeFooterButtons();
    }, []);

    const loadUserPreferences = async () => {
        try {
            setIsLoading(true);
            const userPrefs = await getUserPreferences(
                user.jwt.ind ?? 0,
                user.id,
            );
            setPreferences(userPrefs);
        } catch (error) {
            Toast.error(getText('msg_e434781fe6a7'));
        } finally {
            setIsLoading(false);
        }
    };

    const [categories, setCategories] = useState([]);
    const [restrictedCategoryIds, setRestrictedCategoryIds] = useState([]);

    useEffect(() => {
        const fetchOptOutCategories = async () => {
            setIsLoading(true);
            try {
                const data = await getClientOptions(
                    0,
                    [OPT_OUT_CATEGORIES_CLIENT_OPTION_ID],
                    false,
                );
                const optOutOption = data.find(
                    option =>
                        option.clientOptionId ===
                        OPT_OUT_CATEGORIES_CLIENT_OPTION_ID,
                );
                if (optOutOption) {
                    const items = (optOutOption.selectList || []).map(item => ({
                        id: item.id,
                        name: item.label,
                    }));
                    setCategories(items);
                    if (optOutOption.values) {
                        setRestrictedCategoryIds(optOutOption.values);
                    }
                }
            } catch (error) {
                console.error('Could not fetch opt-out categories:', error);
                Toast.error(getText('msg_fda640defb8d'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchOptOutCategories();
    }, []);

    const initializeFooterButtons = () => {
        if (footerRef.current) {
            const buttons = footerRef.current.querySelectorAll('.btn');
            const maxWidth = Math.max(
                ...Array.from(buttons).map(button => button.offsetWidth),
            );
            buttons.forEach(button => (button.style.width = `${maxWidth}px`));
        }
    };

    const validateEmailAddress = email => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateEmailInRealTime = email => {
        if (!email.trim()) {
            return '';
        }
        if (!validateEmailAddress(email)) {
            return 'Invalid email address';
        }
        return '';
    };

    const validatePhoneInRealTime = phone => {
        if (!phone) {
            return '';
        }
        if (!isPossiblePhoneNumber(phone) || !isValidPhoneNumber(phone)) {
            return 'Invalid phone number';
        }
        return '';
    };

    const handleChannelSelect = methodId => {
        if (
            methodId === NOTIFICATION_METHOD.optionalEmail &&
            !preferences.emailAddress
        ) {
            Toast.error(
                getText('msg_5c4323592566'),
            );
            return;
        }

        if (
            methodId === NOTIFICATION_METHOD.optionalPhone &&
            !preferences.phoneNumber
        ) {
            Toast.error(
                getText('msg_b91179376a51'),
            );
            return;
        }

        setPreferences(prev => ({
            ...prev,
            notificationPreferredMethodId: methodId,
        }));
    };

    const handleOptionalEmailChange = e => {
        const value = e.target.value;
        setPreferences(prev => ({
            ...prev,
            emailAddress: value,
        }));

        const message = validateEmailInRealTime(value);
        setValidationMessages(prev => ({
            ...prev,
            emailAddress: message,
        }));

        if (
            !value &&
            preferences.notificationPreferredMethodId ===
                NOTIFICATION_METHOD.optionalEmail
        ) {
            setPreferences(prev => ({
                ...prev,
                notificationPreferredMethodId: NOTIFICATION_METHOD.primaryEmail,
            }));
        }
    };

    const handleOptionalPhoneChange = value => {
        setPreferences(prev => ({
            ...prev,
            phoneNumber: value,
        }));

        const message = validatePhoneInRealTime(value);
        setValidationMessages(prev => ({
            ...prev,
            phoneNumber: message,
        }));

        if (
            !value &&
            preferences.notificationPreferredMethodId ===
                NOTIFICATION_METHOD.optionalPhone
        ) {
            setPreferences(prev => ({
                ...prev,
                notificationPreferredMethodId: NOTIFICATION_METHOD.primaryEmail,
            }));
        }
    };

    const handleClearOptionalEmail = () => {
        if (
            preferences.notificationPreferredMethodId ===
            NOTIFICATION_METHOD.optionalEmail
        ) {
            setPreferences(prev => ({
                ...prev,
                emailAddress: '',
                notificationPreferredMethodId: NOTIFICATION_METHOD.primaryEmail,
            }));
        } else {
            setPreferences(prev => ({
                ...prev,
                emailAddress: '',
            }));
        }

        setValidationMessages(prev => ({
            ...prev,
            emailAddress: '',
        }));
    };

    const handleClearOptionalPhone = () => {
        if (
            preferences.notificationPreferredMethodId ===
            NOTIFICATION_METHOD.optionalPhone
        ) {
            setPreferences(prev => ({
                ...prev,
                phoneNumber: '',
                notificationPreferredMethodId: NOTIFICATION_METHOD.primaryEmail,
            }));
        } else {
            setPreferences(prev => ({
                ...prev,
                phoneNumber: '',
            }));
        }

        setValidationMessages(prev => ({
            ...prev,
            phoneNumber: '',
        }));
    };

    const handleCategoriesChange = selectedCategories => {
        setPreferences(prev => ({
            ...prev,
            notificationCategoryOptOuts: selectedCategories.map(cat => cat?.id),
        }));
    };

    const handleSave = async () => {
        if (
            preferences.notificationPreferredMethodId ===
                NOTIFICATION_METHOD.pushNotification &&
            !notificationsEnabled
        ) {
            try {
                const permissionResult = await requestNotificationPermission();
                if (!permissionResult) {
                    Toast.error(
                        getText('msg_9e8eaf199b8b'),
                    );
                    return;
                }
            } catch (error) {
                console.error(
                    'Failed to request notification permission:',
                    error,
                );
                Toast.error(
                    getText('msg_c8344e61b7b7'),
                );
                return;
            }
        }

        if (
            preferences.notificationPreferredMethodId ===
            NOTIFICATION_METHOD.optionalEmail
        ) {
            if (!preferences.emailAddress) {
                Toast.error(getText('msg_23b838150624'));
                return;
            }

            const emailMessage = validateEmailInRealTime(
                preferences.emailAddress,
            );
            if (emailMessage) {
                setValidationMessages(prev => ({
                    ...prev,
                    emailAddress: emailMessage,
                }));
                Toast.error(getText('msg_0802635ce77d'));
                return;
            }
        }

        if (
            preferences.notificationPreferredMethodId ===
            NOTIFICATION_METHOD.optionalPhone
        ) {
            if (!preferences.phoneNumber) {
                Toast.error(getText('msg_f002b93e92e4'));
                return;
            }

            const phoneMessage = validatePhoneInRealTime(
                preferences.phoneNumber,
            );
            if (phoneMessage) {
                setValidationMessages(prev => ({
                    ...prev,
                    phoneNumber: phoneMessage,
                }));
                Toast.error(getText('msg_4986d6ae2e57'));
                return;
            }
        }

        try {
            setIsLoading(true);
            await updateCommunicationPreferences(
                user.jwt.ind ?? 0,
                user.id,
                preferences,
            );
            Toast.success(getText('msg_2e34a9263b87'));
            if (isModal) {
                onYes();
            }
            if (onHide) {
                onHide();
            }
        } catch (error) {
            console.error('Failed to update communication preferences', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else Toast.error(getText('msg_34754d5b0d69'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`modal-body ${style.modalBody} m-auto mt-3`}>
            <div className="display-flex align-items-center">
                <h3 className="no-flex font-weight-700 h5">{getText('msg_753a22b2eb61')}</h3>
            </div>
            <div
                className={`${style.securityPreferences}`}
                style={{ marginBottom: '20px' }}
            >
                <p>
                    {getText('msg_a34e5dfa9f7e')}
                </p>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    gap: isModal ? '2px' : '20px',
                }}
                className="w-75 mx-5"
            >
                {/* Primary Email */}
                {preferences.fileEmailAddress && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '8px',
                        }}
                        className={style.contactMethodRadio}
                    >
                        <input
                            type="radio"
                            name="communicationChannel"
                            checked={
                                preferences.notificationPreferredMethodId ===
                                NOTIFICATION_METHOD.primaryEmail
                            }
                            onChange={() =>
                                handleChannelSelect(
                                    NOTIFICATION_METHOD.primaryEmail,
                                )
                            }
                            className="form-check-input m-auto"
                            id="primaryEmail"
                        />
                        <label
                            htmlFor="primaryEmail"
                            className="form-check-label"
                        >
                            {preferences.fileEmailAddress}
                        </label>
                    </div>
                )}
                {preferences.filePhoneNumber && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '8px',
                        }}
                        className={style.contactMethodRadio}
                    >
                        <input
                            type="radio"
                            name="communicationChannel"
                            checked={
                                preferences.notificationPreferredMethodId ===
                                NOTIFICATION_METHOD.primaryPhone
                            }
                            onChange={() =>
                                handleChannelSelect(
                                    NOTIFICATION_METHOD.primaryPhone,
                                )
                            }
                            className="form-check-input m-auto"
                            id="primaryPhone"
                            disabled={!preferences.filePhoneNumber}
                        />
                        <label
                            htmlFor="primaryPhone"
                            className="form-check-label"
                        >
                            {formatPhoneNumber(preferences.filePhoneNumber) ||
                                'N/A'}
                        </label>
                    </div>
                )}

                {/* Push Notifications */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                    }}
                    className={style.contactMethodRadio}
                >
                    <input
                        type="radio"
                        name="communicationChannel"
                        checked={
                            preferences.notificationPreferredMethodId ===
                            NOTIFICATION_METHOD.pushNotification
                        }
                        onChange={() =>
                            handleChannelSelect(
                                NOTIFICATION_METHOD.pushNotification,
                            )
                        }
                        className="form-check-input m-auto"
                        id="pushNotification"
                    />
                    <label
                        htmlFor="pushNotification"
                        className="form-check-label"
                    >
                        {getText('msg_94b8ba097c84')}
                    </label>
                </div>

                {/* Optional Email */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        width: '100%',
                    }}
                    className={style.contactMethodRadio}
                >
                    <input
                        type="radio"
                        name="communicationChannel"
                        checked={
                            preferences.notificationPreferredMethodId ===
                            NOTIFICATION_METHOD.optionalEmail
                        }
                        onChange={() =>
                            handleChannelSelect(
                                NOTIFICATION_METHOD.optionalEmail,
                            )
                        }
                        className="form-check-input m-auto"
                        id="optionalEmail"
                    />
                    <div className="w-100 m-auto position-relative">
                        <input
                            onChange={handleOptionalEmailChange}
                            className={`form-control textInput ${
                                validationMessages.emailAddress
                                    ? 'is-invalid'
                                    : ''
                            }`}
                            type="email"
                            placeholder={getText('msg_da93051b1eb9')}
                            value={preferences.emailAddress || ''}
                        />
                        {preferences.emailAddress && (
                            <button
                                type="button"
                                className="btn btn-link position-absolute end-0 top-0 text-danger"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    height: '100%',
                                    padding: '0 10px',
                                }}
                                onClick={handleClearOptionalEmail}
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        )}
                        {validationMessages.emailAddress && (
                            <div className="invalid-feedback">
                                {validationMessages.emailAddress}
                            </div>
                        )}
                    </div>
                </div>

                {/* Optional Phone */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        width: '100%',
                    }}
                    className={style.contactMethodRadio}
                >
                    <input
                        type="radio"
                        name="communicationChannel"
                        checked={
                            preferences.notificationPreferredMethodId ===
                            NOTIFICATION_METHOD.optionalPhone
                        }
                        onChange={() =>
                            handleChannelSelect(
                                NOTIFICATION_METHOD.optionalPhone,
                            )
                        }
                        className="form-check-input m-auto"
                        id="optionalPhone"
                    />
                    <div className="w-100 m-auto position-relative">
                        <PhoneInput
                            country="US"
                            className={`form-control ${
                                validationMessages.phoneNumber
                                    ? 'is-invalid'
                                    : ''
                            }`}
                            value={preferences.phoneNumber || ''}
                            onChange={handleOptionalPhoneChange}
                            placeholder={getText('msg_4c7ae7eefca7')}
                        />
                        {preferences.phoneNumber && (
                            <button
                                type="button"
                                className="btn btn-link position-absolute end-0 top-0 text-danger"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    height: '100%',
                                    padding: '0 10px',
                                }}
                                onClick={handleClearOptionalPhone}
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        )}
                        {validationMessages.phoneNumber && (
                            <div className="invalid-feedback">
                                {validationMessages.phoneNumber}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-5">
                <label htmlFor="optOutCategories" className="mb-2">
                    {getText('msg_60dad3c2ab38')}
                    <Tooltip
                        html="Select categories you wish to opt out from."
                        placement="right"
                    >
                        <i
                            className="bi bi-info-circle"
                            style={{
                                paddingLeft: '5px',
                                fontSize: '13px',
                                color: 'var(--iNGAGED-Primary-Color)',
                            }}
                        ></i>
                    </Tooltip>
                </label>
                <LoadingWrapper isLoading={isLoading}>
                    <SearchableDropdown
                        id="optOutCategories"
                        showToolTip={true}
                        tooltipText={getText('msg_d282cbeb2a08')}
                        options={categories.filter(
                            cat =>
                                !restrictedCategoryIds.includes(cat.id) ||
                                preferences.notificationCategoryOptOuts.includes(
                                    cat.id,
                                ),
                        )}
                        optionIDField="id"
                        savedOptions={preferences.notificationCategoryOptOuts.map(
                            id => ({ id }),
                        )}
                        savedOptionIDField="id"
                        updateOptions={handleCategoriesChange}
                    />
                </LoadingWrapper>
            </div>

            {/* Footer */}
            <div
                className={`d-flex justify-content-evenly mt-5`}
                ref={footerRef}
            >
                {isModal && (
                    <button
                        type="button"
                        className="ing-popup-blue-negative"
                        onClick={onHide}
                        disabled={isLoading}
                    >
                        {getText('msg_77dfd2135f4d')}
                    </button>
                )}
                <button
                    type="button"
                    className="ing-button-primary-none"
                    onClick={handleSave}
                    disabled={isLoading}
                >
                    {isLoading ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    );
};

export default CommunicationPreferences;
